import {
  INSPECTION_QUESTION_KINDS,
  type InspectionQuestion,
  type InspectionQuestionKind,
  type InspectionSchema,
  type InspectionSection,
  type InspectionSelectOption,
} from '@sitewise/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Input,
  Label,
  Skeleton,
  toast,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  ArrowUpFromLine,
  Edit2,
  FileCheck2,
  Plus,
  Save,
  Trash2,
} from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { fetchMe } from '../../../api/auth.js';
import {
  archiveInspectionTemplate,
  createDraftVersion,
  fetchInspectionTemplate,
  publishVersion,
  updateDraftVersion,
  updateInspectionTemplate,
  type InspectionTemplateVersionSummary,
} from '../../../api/inspection-templates.js';
import { ApiError } from '../../../lib/api-client.js';

import { appliesToLabel, formatDate, questionKindLabel, TemplateStatusBadge } from './lib.js';

function newId(): string {
  return crypto.randomUUID();
}

function emptyQuestion(kind: InspectionQuestionKind): InspectionQuestion {
  const base = { id: newId(), label: 'New question', required: false };
  switch (kind) {
    case 'yes_no':
      return { ...base, kind, defectIf: 'no' };
    case 'pass_fail':
      return { ...base, kind };
    case 'rating':
      return { ...base, kind, scale: 5, defectAtOrBelow: 2 };
    case 'short_text':
      return { ...base, kind };
    case 'long_text':
      return { ...base, kind };
    case 'number':
      return { ...base, kind };
    case 'single_select':
      return { ...base, kind, options: [{ value: 'option-1', label: 'Option 1' }] };
    case 'multi_select':
      return { ...base, kind, options: [{ value: 'option-1', label: 'Option 1' }] };
    case 'photo':
      return { ...base, kind, minPhotos: 1 };
    case 'signature':
      return { ...base, kind };
    case 'section_header':
      return { id: newId(), kind, label: 'Subheading' };
  }
}

export function InspectionTemplateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const meQuery = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const isAdmin = meQuery.data?.user.role === 'admin';

  const detailQuery = useQuery({
    queryKey: ['inspection-templates', id],
    queryFn: () => fetchInspectionTemplate(id!),
    enabled: Boolean(id),
  });

  // Local working schema. Synced from the latest draft version (or the
  // latest published version if no draft exists). Edits are saved
  // explicitly via "Save draft".
  const [draft, setDraft] = useState<InspectionTemplateVersionSummary | null>(null);
  const [schema, setSchema] = useState<InspectionSchema>({ sections: [] });
  const [editMetaOpen, setEditMetaOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<{
    sectionId: string;
    question: InspectionQuestion;
  } | null>(null);

  useEffect(() => {
    if (!detailQuery.data) return;
    const versions = detailQuery.data.versions;
    const latestDraft =
      [...versions].reverse().find((v) => v.publishedAt === null) ?? null;
    const latestPublished =
      [...versions].reverse().find((v) => v.publishedAt !== null) ?? null;
    const seed = latestDraft ?? latestPublished;
    setDraft(latestDraft);
    setSchema((seed?.schema as InspectionSchema) ?? { sections: [] });
  }, [detailQuery.data]);

  const createDraftMut = useMutation({
    mutationFn: () => createDraftVersion(id!),
    onSuccess: (r) => {
      setDraft(r.version);
      setSchema(r.version.schema as InspectionSchema);
      toast.success('Draft created.');
      void queryClient.invalidateQueries({ queryKey: ['inspection-templates', id] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not create draft');
    },
  });

  const saveDraftMut = useMutation({
    mutationFn: async () => {
      if (!draft) throw new Error('No draft version');
      return updateDraftVersion(id!, draft.id, { schema });
    },
    onSuccess: (r) => {
      setDraft(r.version);
      toast.success('Draft saved.');
      void queryClient.invalidateQueries({ queryKey: ['inspection-templates', id] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not save draft');
    },
  });

  const publishMut = useMutation({
    mutationFn: async () => {
      if (!draft) throw new Error('No draft to publish');
      // Save first so the latest edits are persisted before the publish
      // endpoint snapshots them.
      await updateDraftVersion(id!, draft.id, { schema });
      return publishVersion(id!, draft.id);
    },
    onSuccess: () => {
      toast.success('Template published.');
      void queryClient.invalidateQueries({ queryKey: ['inspection-templates'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not publish');
    },
  });

  const archiveMut = useMutation({
    mutationFn: () => archiveInspectionTemplate(id!),
    onSuccess: () => {
      toast.success('Template archived.');
      void queryClient.invalidateQueries({ queryKey: ['inspection-templates'] });
      navigate('/inspections/templates');
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not archive');
    },
  });

  if (detailQuery.isError) {
    return <ErrorState description="Could not load template." />;
  }
  if (detailQuery.isLoading || !detailQuery.data) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  const { template } = detailQuery.data;

  function updateSection(sectionId: string, patch: Partial<InspectionSection>) {
    setSchema((prev) => ({
      ...prev,
      sections: prev.sections.map((s) => (s.id === sectionId ? { ...s, ...patch } : s)),
    }));
  }

  function addSection() {
    setSchema((prev) => ({
      ...prev,
      sections: [
        ...prev.sections,
        { id: newId(), title: `Section ${prev.sections.length + 1}`, questions: [] },
      ],
    }));
  }

  function removeSection(sectionId: string) {
    if (!confirm('Delete this section and all its questions?')) return;
    setSchema((prev) => ({
      ...prev,
      sections: prev.sections.filter((s) => s.id !== sectionId),
    }));
  }

  function moveSection(sectionId: string, dir: -1 | 1) {
    setSchema((prev) => {
      const idx = prev.sections.findIndex((s) => s.id === sectionId);
      if (idx === -1) return prev;
      const j = idx + dir;
      if (j < 0 || j >= prev.sections.length) return prev;
      const sections = [...prev.sections];
      [sections[idx], sections[j]] = [sections[j]!, sections[idx]!];
      return { ...prev, sections };
    });
  }

  function addQuestion(sectionId: string, kind: InspectionQuestionKind) {
    setSchema((prev) => ({
      ...prev,
      sections: prev.sections.map((s) =>
        s.id === sectionId
          ? { ...s, questions: [...s.questions, emptyQuestion(kind)] }
          : s,
      ),
    }));
  }

  function removeQuestion(sectionId: string, questionId: string) {
    if (!confirm('Delete this question?')) return;
    setSchema((prev) => ({
      ...prev,
      sections: prev.sections.map((s) =>
        s.id === sectionId
          ? { ...s, questions: s.questions.filter((q) => q.id !== questionId) }
          : s,
      ),
    }));
  }

  function moveQuestion(sectionId: string, questionId: string, dir: -1 | 1) {
    setSchema((prev) => ({
      ...prev,
      sections: prev.sections.map((s) => {
        if (s.id !== sectionId) return s;
        const idx = s.questions.findIndex((q) => q.id === questionId);
        if (idx === -1) return s;
        const j = idx + dir;
        if (j < 0 || j >= s.questions.length) return s;
        const questions = [...s.questions];
        [questions[idx], questions[j]] = [questions[j]!, questions[idx]!];
        return { ...s, questions };
      }),
    }));
  }

  function applyQuestionPatch(sectionId: string, q: InspectionQuestion) {
    setSchema((prev) => ({
      ...prev,
      sections: prev.sections.map((s) =>
        s.id === sectionId
          ? { ...s, questions: s.questions.map((qq) => (qq.id === q.id ? q : qq)) }
          : s,
      ),
    }));
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="icon" aria-label="Back">
          <Link to="/inspections/templates">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h1 className="truncate text-xl font-semibold">{template.name}</h1>
            <TemplateStatusBadge status={template.status} />
            {template.isArchived ? <Badge variant="outline">Archived</Badge> : null}
          </div>
          <div className="text-xs text-fg-muted">
            {appliesToLabel(template.appliesTo)} · v{template.currentVersion ?? '—'}
            {template.publishedVersion
              ? ` · published v${template.publishedVersion}`
              : ' · not published'}
            {' · '}updated {formatDate(template.updatedAt)}
          </div>
        </div>
        {isAdmin ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditMetaOpen(true)}>
              <Edit2 className="h-4 w-4" />
              Edit metadata
            </Button>
            {!draft ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => createDraftMut.mutate()}
                disabled={createDraftMut.isPending}
              >
                <Plus className="h-4 w-4" />
                {createDraftMut.isPending ? 'Creating…' : 'New draft'}
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => saveDraftMut.mutate()}
                  disabled={saveDraftMut.isPending}
                >
                  <Save className="h-4 w-4" />
                  {saveDraftMut.isPending ? 'Saving…' : 'Save draft'}
                </Button>
                <Button
                  size="sm"
                  onClick={() => publishMut.mutate()}
                  disabled={publishMut.isPending || schema.sections.length === 0}
                >
                  <ArrowUpFromLine className="h-4 w-4" />
                  {publishMut.isPending ? 'Publishing…' : 'Publish'}
                </Button>
              </>
            )}
            {!template.isArchived ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (confirm('Archive this template?')) archiveMut.mutate();
                }}
                disabled={archiveMut.isPending}
              >
                <Trash2 className="h-4 w-4" />
                Archive
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {!draft && template.publishedVersion ? (
        <div className="rounded border border-info-subtle bg-info-subtle/30 px-3 py-2 text-sm text-info">
          You're viewing the published v{template.publishedVersion}. Click <strong>New draft</strong>{' '}
          to make structural changes.
        </div>
      ) : null}

      {schema.sections.length === 0 ? (
        <EmptyState
          icon={FileCheck2}
          title="Empty template"
          description="Add a section to start composing the form. Sections group related questions."
          action={
            isAdmin && draft ? (
              <Button onClick={addSection}>
                <Plus className="h-4 w-4" />
                Add section
              </Button>
            ) : null
          }
        />
      ) : (
        <div className="space-y-4">
          {schema.sections.map((section, sIdx) => (
            <Card key={section.id}>
              <CardHeader className="flex flex-row items-start justify-between gap-2 pb-3">
                <div className="min-w-0 flex-1">
                  <Input
                    value={section.title}
                    onChange={(e) => updateSection(section.id, { title: e.target.value })}
                    disabled={!isAdmin || !draft}
                    className="font-medium"
                  />
                  <textarea
                    rows={2}
                    placeholder="Section description (optional)"
                    className="mt-2 w-full rounded border border-border bg-surface px-3 py-2 text-sm text-fg-secondary"
                    value={section.description ?? ''}
                    onChange={(e) =>
                      updateSection(section.id, { description: e.target.value || undefined })
                    }
                    disabled={!isAdmin || !draft}
                  />
                </div>
                {isAdmin && draft ? (
                  <div className="flex items-center gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => moveSection(section.id, -1)}
                      disabled={sIdx === 0}
                      aria-label="Move section up"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => moveSection(section.id, 1)}
                      disabled={sIdx === schema.sections.length - 1}
                      aria-label="Move section down"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => removeSection(section.id)}
                      aria-label="Delete section"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ) : null}
              </CardHeader>
              <CardContent className="space-y-2">
                {section.questions.map((q, qIdx) => (
                  <div
                    key={q.id}
                    className="flex items-start gap-2 rounded border border-border bg-surface px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">
                          {(q as { label: string }).label}
                        </span>
                        <Badge variant="outline" className="text-[10px]">
                          {questionKindLabel(q.kind)}
                        </Badge>
                        {(q as { required?: boolean }).required ? (
                          <span className="text-xs text-danger">required</span>
                        ) : null}
                      </div>
                      {(q as { helpText?: string }).helpText ? (
                        <div className="mt-0.5 text-xs text-fg-muted">
                          {(q as { helpText?: string }).helpText}
                        </div>
                      ) : null}
                    </div>
                    {isAdmin && draft ? (
                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => moveQuestion(section.id, q.id, -1)}
                          disabled={qIdx === 0}
                          aria-label="Move up"
                        >
                          <ArrowUp className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => moveQuestion(section.id, q.id, 1)}
                          disabled={qIdx === section.questions.length - 1}
                          aria-label="Move down"
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setEditingQuestion({ sectionId: section.id, question: q })}
                          aria-label="Edit"
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => removeQuestion(section.id, q.id)}
                          aria-label="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ) : null}
                  </div>
                ))}

                {isAdmin && draft ? (
                  <AddQuestionMenu sectionId={section.id} onAdd={addQuestion} />
                ) : null}
              </CardContent>
            </Card>
          ))}

          {isAdmin && draft ? (
            <Button variant="outline" onClick={addSection}>
              <Plus className="h-4 w-4" />
              Add section
            </Button>
          ) : null}
        </div>
      )}

      {editMetaOpen ? (
        <EditMetadataDialog
          template={template}
          onClose={() => setEditMetaOpen(false)}
          onSaved={() => {
            setEditMetaOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['inspection-templates', id] });
          }}
        />
      ) : null}

      {editingQuestion ? (
        <EditQuestionDialog
          question={editingQuestion.question}
          onClose={() => setEditingQuestion(null)}
          onSave={(q) => {
            applyQuestionPatch(editingQuestion.sectionId, q);
            setEditingQuestion(null);
          }}
        />
      ) : null}
    </div>
  );
}

function AddQuestionMenu({
  sectionId,
  onAdd,
}: {
  sectionId: string;
  onAdd: (sectionId: string, kind: InspectionQuestionKind) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      {!open ? (
        <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
          <Plus className="h-4 w-4" />
          Add question
        </Button>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5 rounded border border-dashed border-border p-2">
          {INSPECTION_QUESTION_KINDS.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => {
                onAdd(sectionId, k);
                setOpen(false);
              }}
              className="rounded bg-surface-muted px-2.5 py-1 text-xs font-medium hover:bg-primary-subtle hover:text-primary"
            >
              {questionKindLabel(k)}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="ml-1 text-xs text-fg-muted hover:underline"
          >
            cancel
          </button>
        </div>
      )}
    </div>
  );
}

function EditMetadataDialog({
  template,
  onClose,
  onSaved,
}: {
  template: { id: string; name: string; description: string | null };
  onClose: () => void;
  onSaved: () => void;
}) {
  const nameId = useId();
  const descId = useId();
  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description ?? '');
  const update = useMutation({
    mutationFn: () =>
      updateInspectionTemplate(template.id, {
        name: name.trim(),
        description: description.trim() || null,
      }),
    onSuccess: () => {
      toast.success('Template updated.');
      onSaved();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not update');
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit template</DialogTitle>
          <DialogDescription>Metadata only. Use Save draft for schema changes.</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            update.mutate();
          }}
          className="space-y-3"
        >
          <div className="grid gap-1.5">
            <Label htmlFor={nameId}>Name</Label>
            <Input
              id={nameId}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              required
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor={descId}>Description</Label>
            <textarea
              id={descId}
              rows={3}
              maxLength={2_000}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="rounded border border-border bg-surface px-3 py-2 text-sm"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending || !name.trim()}>
              {update.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditQuestionDialog({
  question,
  onClose,
  onSave,
}: {
  question: InspectionQuestion;
  onClose: () => void;
  onSave: (q: InspectionQuestion) => void;
}) {
  const [draft, setDraft] = useState<InspectionQuestion>(question);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit question</DialogTitle>
          <DialogDescription>{questionKindLabel(question.kind)}</DialogDescription>
        </DialogHeader>
        <QuestionEditor draft={draft} onChange={setDraft} />
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => onSave(draft)}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QuestionEditor({
  draft,
  onChange,
}: {
  draft: InspectionQuestion;
  onChange: (next: InspectionQuestion) => void;
}) {
  function patch<K extends InspectionQuestion['kind']>(
    extra: Partial<Extract<InspectionQuestion, { kind: K }>>,
  ) {
    onChange({ ...(draft as Extract<InspectionQuestion, { kind: K }>), ...extra });
  }

  return (
    <div className="space-y-3 text-sm">
      <div className="grid gap-1.5">
        <Label>Label</Label>
        <Input
          value={(draft as { label: string }).label}
          onChange={(e) => patch({ label: e.target.value } as never)}
          maxLength={300}
        />
      </div>

      {draft.kind !== 'section_header' ? (
        <>
          <div className="grid gap-1.5">
            <Label>Help text</Label>
            <Input
              value={(draft as { helpText?: string }).helpText ?? ''}
              onChange={(e) => patch({ helpText: e.target.value || undefined } as never)}
              maxLength={500}
              placeholder="Visible under the question on the runner"
            />
          </div>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={Boolean((draft as { required?: boolean }).required)}
              onChange={(e) => patch({ required: e.target.checked } as never)}
            />
            <span>Required</span>
          </label>
        </>
      ) : null}

      {draft.kind === 'yes_no' ? (
        <div className="grid gap-1.5">
          <Label>Treat as defect when answer is</Label>
          <select
            className="rounded border border-border bg-surface px-3 py-2 text-sm"
            value={draft.defectIf ?? 'no'}
            onChange={(e) => patch<'yes_no'>({ defectIf: e.target.value as 'yes' | 'no' })}
          >
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
        </div>
      ) : null}

      {draft.kind === 'rating' ? (
        <div className="grid grid-cols-2 gap-3">
          <div className="grid gap-1.5">
            <Label>Scale</Label>
            <select
              className="rounded border border-border bg-surface px-3 py-2 text-sm"
              value={draft.scale}
              onChange={(e) => patch<'rating'>({ scale: Number(e.target.value) as 3 | 5 })}
            >
              <option value={3}>1 – 3</option>
              <option value={5}>1 – 5</option>
            </select>
          </div>
          <div className="grid gap-1.5">
            <Label>Defect at or below</Label>
            <Input
              type="number"
              min={1}
              max={draft.scale}
              value={draft.defectAtOrBelow ?? ''}
              onChange={(e) =>
                patch<'rating'>({
                  defectAtOrBelow: e.target.value ? Number(e.target.value) : undefined,
                })
              }
            />
          </div>
        </div>
      ) : null}

      {draft.kind === 'number' ? (
        <div className="grid grid-cols-3 gap-3">
          <div className="grid gap-1.5">
            <Label>Unit</Label>
            <Input
              value={draft.unit ?? ''}
              onChange={(e) => patch<'number'>({ unit: e.target.value || undefined })}
            />
          </div>
          <div className="grid gap-1.5">
            <Label>Min</Label>
            <Input
              type="number"
              value={draft.min ?? ''}
              onChange={(e) =>
                patch<'number'>({ min: e.target.value ? Number(e.target.value) : undefined })
              }
            />
          </div>
          <div className="grid gap-1.5">
            <Label>Max</Label>
            <Input
              type="number"
              value={draft.max ?? ''}
              onChange={(e) =>
                patch<'number'>({ max: e.target.value ? Number(e.target.value) : undefined })
              }
            />
          </div>
        </div>
      ) : null}

      {draft.kind === 'photo' ? (
        <div className="grid grid-cols-2 gap-3">
          <div className="grid gap-1.5">
            <Label>Min photos</Label>
            <Input
              type="number"
              min={0}
              value={draft.minPhotos ?? ''}
              onChange={(e) =>
                patch<'photo'>({
                  minPhotos: e.target.value ? Number(e.target.value) : undefined,
                })
              }
            />
          </div>
          <div className="grid gap-1.5">
            <Label>Max photos</Label>
            <Input
              type="number"
              min={1}
              value={draft.maxPhotos ?? ''}
              onChange={(e) =>
                patch<'photo'>({
                  maxPhotos: e.target.value ? Number(e.target.value) : undefined,
                })
              }
            />
          </div>
        </div>
      ) : null}

      {draft.kind === 'single_select' || draft.kind === 'multi_select' ? (
        <SelectOptionsEditor
          options={draft.options}
          onChange={(options) => onChange({ ...draft, options } as InspectionQuestion)}
        />
      ) : null}
    </div>
  );
}

function SelectOptionsEditor({
  options,
  onChange,
}: {
  options: InspectionSelectOption[];
  onChange: (options: InspectionSelectOption[]) => void;
}) {
  return (
    <div className="grid gap-2">
      <Label>Options</Label>
      {options.map((opt, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <Input
            placeholder="Label"
            value={opt.label}
            onChange={(e) => {
              const next = [...options];
              next[idx] = { ...next[idx]!, label: e.target.value };
              onChange(next);
            }}
          />
          <Input
            placeholder="Value"
            value={opt.value}
            onChange={(e) => {
              const next = [...options];
              next[idx] = { ...next[idx]!, value: e.target.value };
              onChange(next);
            }}
            className="w-32"
          />
          <label className="flex items-center gap-1.5 text-xs">
            <input
              type="checkbox"
              checked={Boolean(opt.isDefect)}
              onChange={(e) => {
                const next = [...options];
                next[idx] = { ...next[idx]!, isDefect: e.target.checked || undefined };
                onChange(next);
              }}
            />
            defect
          </label>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={() => {
              onChange(options.filter((_, i) => i !== idx));
            }}
            aria-label="Remove option"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() =>
          onChange([
            ...options,
            { value: `option-${options.length + 1}`, label: `Option ${options.length + 1}` },
          ])
        }
      >
        <Plus className="h-4 w-4" />
        Add option
      </Button>
    </div>
  );
}
